
import { BlogPost } from "@/types/blog";

export const post: BlogPost = {
  slug: "municipal-codes-that-lead-to-permit-rejections",
  title: "Municipal Codes that Lead to Permit Rejections",
  excerpt: "Learn about specific municipal codes across different cities that commonly cause permit rejections for construction projects.",
  content: `
    <h2>Recap</h2>
    
    <p>
      In our last article, we discussed the challenges that builders and developers face while navigating the world of permitting. 
      We explored the monetary impact on builders and developers, as well as some of the upcoming regulatory changes that will 
      impact these construction markets in the near future. Below we'll dive further into specific codes across the country that 
      cause permit rejections.
    </p>
    
    <h2>Code Rejections per Municipality</h2>
    
    <p>
      Since codes vary from city-to-city and state-to-state, every city's rejection rates also vary. However, there are certain 
      municipal codes in every geography that often lead to a poor permitting outcome. The list below is not exhaustive, but 
      a good starting place to understand if you're building in a new area or if you've had previous rejections in any of these 
      municipalities. Also, see snips of the Code Clarifier in action for a handful of these!
    </p>
    
    <h3>Los Angeles, California</h3>
    
    <p>
      For residential housing in the City of Los Angeles, builders commonly miss the specific seismic retrofit requirements when 
      building a living space above a garage or other open-concept ground floor. The following sections detail out the requirements 
      for analyzing and retrofitting soft-story buildings to resist seismic forces. Missing this can result in permit rejection due 
      to non-compliance with seismic safety standards.
    </p>
    
    <p>
      Applicable Codes:
    </p>
    <ul>
      <li>91.5.301.1.3.2: Woodframe Structures</li>
      <li>91.5.301.1.5: Seismic Design Provisions for Buildings on Slopes</li>
      <li>1613.6: Scope and Requirements for Lateral-Force-Resisting Systems</li>
    </ul>
    
    <h3>Boston, Massachusetts</h3>
    
    <p>
      In Boston's historic districts, developers and building retro-fitters often overlook the requirements for preserving historic 
      building facades or adhering to specific design guidelines that maintain the historical character of the area. Any changes to 
      the exterior of historic buildings must be approved by the local historical commission. Developers who fail to submit 
      appropriate plans that preserve historical elements or who propose non-compliant alterations risk permit rejections and delays.
    </p>
    
    <p>
      Applicable Codes:
    </p>
    <ul>
      <li>Sec. 45-17: Protection Area Design Review and Design Guidelines</li>
      <li>Sec. 44-7: Specific Design Requirements</li>
    </ul>
    
    <h3>San Diego, California</h3>
    
    <p>
      Development projects within San Diego's Coastal Zone require a Coastal Development Permit (CDP). Developers often miss the 
      extensive documentation required to demonstrate compliance with coastal protection laws, including public access, environmental 
      preservation, and building height restrictions. Inadequate documentation or failure to secure a CDP can result in permit rejections.
    </p>
    
    <p>
      Applicable Codes:
    </p>
    <ul>
      <li>Chapter 12, Article 6, Division 7: Coastal Development Permit Procedures</li>
      <li>Chapter 14, Article 3, Division 1: Environmentally Sensitive Lands Regulations</li>
      <li>Chapter 14, Article 3, Division 1: Development Regulations for Special Flood Hazard Areas</li>
    </ul>
    
    <h3>Miami, Florida</h3>
    
    <p>
      When building in areas designated as high-risk flood zones in Miami, stringent floodplain management and elevation 
      requirements are required. The city requires that residential buildings in these areas be elevated to a certain height 
      above the base flood elevation (BFE). Additionally, requirements for anchoring and materials exist in order to prevent flood damage.
    </p>
    
    <p>
      Applicable Codes:
    </p>
    <ul>
      <li>Sec. 11C-3: Development in either Special Flood Hazard Areas, Coastal High Hazard Areas, or Outside Special Flood Hazard Areas</li>
      <li>Sec. 11C-4: Development within Coastal High Hazard Areas</li>
      <li>Sec. 11C-5: Development within Special Flood Hazard Areas</li>
    </ul>
    
    <h3>San Francisco, California</h3>
    
    <p>
      In San Francisco, many developers fail to meet fire safety and egress requirements, particularly in multi-family residential 
      buildings. One common oversight is inadequate egress windows in bedrooms, which must meet specific size and operability 
      criteria to allow occupants to escape in an emergency. Windows that do not meet the minimum size (at least 5.7 square feet, 
      with a minimum opening height of 24 inches and width of 20 inches) or fail to provide clear access to the outdoors can lead to permit rejections.
    </p>
    
    <p>
      Applicable Codes:
    </p>
    <ul>
      <li>Sec. 1030: Emergency Escape and Rescue</li>
    </ul>
    
    <p>
      If you're a developer, builder, solar/storage installer, electrician, or any other trade that comes in contact with municipal 
      codes, doing your research beforehand will not only save you time in the long-run, but also lower your soft costs.
    </p>
    
    <h2>Learn More!</h2>
    
    <p>
      In the next article in the series, learn how to lower barriers to entry when expanding your geographic footprint. We will discuss 
      ways to speed up the code/requirement research and decrease the cost impacts of growing into a new region that you plan on serving.
    </p>
  `,
  coverImage: "/lovable-uploads/c92125ca-466a-44b2-9873-c394c93142d1.png", // Municipal code permit rejection illustration
  date: "2024-09-19T12:00:00",
  readTime: 7,
  categories: ["AI", "Construction", "Regulations", "Permits"],
  author: {
    name: "Richie Rasor",
    title: "Industry Expert",
    avatar: "/lovable-uploads/26c946ec-4296-4ac9-9351-78321594ea25.png"
  }
};
